import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, CheckCircle2, Inbox, Mail, Send, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { FeedbackMessage } from '../types';
import { formatTime } from '../utils';
import { Badge, Button, CollapsiblePanel, EmptyBlock, Field, LoadingBlock, PageHeader, Panel, Select, Textarea, TextInput } from '../components/ui';

const categories = [
  { value: 'bug', label: '问题反馈' },
  { value: 'suggestion', label: '功能建议' },
  { value: 'account', label: '账号问题' },
  { value: 'trade', label: '交易纠纷' },
  { value: 'other', label: '其他' }
];

const statusMeta = {
  sent: { label: '已发送', tone: 'good' as const },
  failed: { label: '邮件失败', tone: 'danger' as const },
  pending: { label: '处理中', tone: 'warn' as const }
};

export function FeedbackPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, settings, permissions } = useAuth();
  const { toast } = useUi();
  const [category, setCategory] = useState('bug');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ id: string; delivered: boolean; message: string } | null>(null);
  const [history, setHistory] = useState<FeedbackMessage[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    if (!permissions.isStaff) return;
    setHistoryLoading(true);
    try {
      const data = await api<{ feedback: FeedbackMessage[] }>('/api/admin/feedback?limit=100');
      setHistory(data.feedback);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '反馈记录加载失败', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (permissions.isStaff) void loadHistory();
  }, [permissions.isStaff, user?.id]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return onNeedAuth();
    setSubmitting(true);
    setResult(null);
    try {
      const data = await api<{ id: string; delivered: boolean; message: string }>('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({ category, subject, content, contactEmail })
      });
      setResult(data);
      setSubject('');
      setContent('');
      setContactEmail('');
      if (permissions.isStaff) void loadHistory();
      toast(data.delivered ? '反馈邮件已发送' : '反馈已保存，管理员可在站内查看', data.delivered ? 'success' : 'info');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '反馈提交失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <>
        <PageHeader title="意见反馈" description="向管理员提交问题、建议或交易反馈。" />
        <EmptyBlock title="登录后才能提交反馈" description="游客可以浏览网站，但提交反馈需要登录账号。" action={<Button variant="primary" onClick={onNeedAuth}>登录 / 注册</Button>} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="意见反馈" description="反馈会发送到管理员配置的邮箱，同时保存在站内供管理员核对。" />
      <div className="feedback-layout">
        <Panel title="提交反馈" eyebrow="CONTACT ADMIN" action={<Badge tone={settings.feedbackEnabled ? 'good' : 'muted'}>{settings.feedbackEnabled ? '邮件反馈已开启' : '邮件反馈未开启'}</Badge>}>
          {!settings.feedbackEnabled ? (
            <EmptyBlock title="管理员尚未开启邮件反馈" description="可以稍后再来，或先通过留言板联系管理员。" />
          ) : (
            <form className="stack-form" onSubmit={submit}>
              <div className="form-note"><ShieldCheck size={17} /><span>反馈内容仅供管理员处理。当前账号：{user.displayName}（@{user.username}），提交后无法编辑。</span></div>
              <div className="form-grid">
                <Field label="反馈类型"><Select value={category} onChange={event => setCategory(event.target.value)} disabled={submitting}>{categories.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</Select></Field>
                <Field label="联系邮箱（可选）" hint="方便管理员回复你，不会公开显示"><TextInput type="email" value={contactEmail} onChange={event => setContactEmail(event.target.value)} placeholder="you@example.com" maxLength={200} disabled={submitting} /></Field>
              </div>
              <Field label="反馈标题"><TextInput value={subject} onChange={event => setSubject(event.target.value)} minLength={1} maxLength={80} placeholder="用一句话概括问题" required disabled={submitting} /></Field>
              <Field label="详细内容" hint="至少 5 个字，请描述发生了什么、你期望的结果，以及能复现问题的操作步骤"><Textarea value={content} onChange={event => setContent(event.target.value)} minLength={5} maxLength={3000} rows={9} placeholder="详细描述你的问题或建议…" required disabled={submitting} /></Field>
              <div className="inline-actions"><Button variant="primary" type="submit" loading={submitting}><Send size={16} /> 提交并发送邮件</Button><span className="muted small">提交频率限制：每人每分钟 1 次，每天最多 10 次</span></div>
            </form>
          )}
          {result && (
            <div className={'feedback-result ' + (result.delivered ? 'success' : 'warning')}>
              {result.delivered ? <CheckCircle2 size={21} /> : <AlertTriangle size={21} />}
              <div><strong>{result.delivered ? '反馈已发送' : '反馈已记录'}</strong><p>{result.message}</p><small>反馈编号：{result.id.slice(0, 8)}</small></div>
            </div>
          )}
        </Panel>

        <aside className="feedback-side">
          <Panel title="处理说明" eyebrow="WORKFLOW">
            <div className="policy-list">
              <p><Mail size={17} /><span><strong>邮件投递</strong>管理员配置 SMTP 后，反馈会直接进入指定邮箱。</span></p>
              <p><Inbox size={17} /><span><strong>站内留档</strong>即使邮件发送失败，反馈内容也不会丢失。</span></p>
              <p><ShieldCheck size={17} /><span><strong>隐私</strong>联系邮箱不会公开，也不会展示给其他玩家。</span></p>
            </div>
          </Panel>
          <Panel title="反馈范围" eyebrow="SUPPORT">
            <p className="muted">账号与绑定、委托交易、市场订单、拍卖权限、网站 Bug、功能建议和游戏内相关问题都可以提交。涉及资金争议时请附上订单号或委托标题。</p>
          </Panel>
        </aside>
      </div>

      {permissions.isStaff && (
        <CollapsiblePanel title="反馈记录" eyebrow="ADMIN INBOX" className="top-gap" defaultOpen={false} action={<Button variant="ghost" type="button" onClick={() => void loadHistory()} disabled={historyLoading}>刷新记录</Button>}>
          {historyLoading && !history ? <LoadingBlock label="正在加载反馈记录" /> : !history?.length ? <EmptyBlock title="还没有玩家反馈" /> : (
            <div className="feedback-history">
              {history.map(item => {
                const state = statusMeta[item.status] || statusMeta.pending;
                return (
                  <article className="feedback-message-card" key={item.id}>
                    <header><div><Badge tone={state.tone}>{state.label}</Badge><strong>{item.subject}</strong></div><small>{formatTime(item.createdAt)}</small></header>
                    <div className="feedback-message-meta"><span>{item.categoryLabel}</span><span>{item.displayName}（@{item.username}）</span><span>{item.contactEmail ? '联系邮箱：' + item.contactEmail : '未填写联系邮箱'}</span></div>
                    <p>{item.content}</p>
                    {item.error && <small className="feedback-error">邮件错误：{item.error}</small>}
                  </article>
                );
              })}
            </div>
          )}
        </CollapsiblePanel>
      )}
    </>
  );
}
